import { useEffect, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '../contexts/hooks';
import { useApiOrders } from '../api/api.orders';
import { setFilteredOrders, setPanelVisible, setFilteresToOrders, setFiltersToOrdersText, setCurrentActiveTab } from '../contexts/slices/orders';
import { useLocation } from 'react-router-dom';
import { generateFiltersText } from '../utils/functions';
import { LIS_States } from '../utils/systemTypes';

/**
 * Hook personalizado para manejar la lógica de filtros automáticos
 * Ejecuta búsquedas automáticas cuando se modifican los filtros con Redirect: true
 */
export const useFilterAutoExecution = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { GetOrdersByFilter, findOrder } = useApiOrders();
  const currentFilters = useAppSelector((x) => x.globalOrders.FiltersToOrders);
  const executedFiltersRef = useRef<string | null>(null); // Trackear filtros ya ejecutados
  const LstEstudios = useAppSelector((x) => x.configuration.Data?.Estudios);
  const LstAreas = useAppSelector((x) => x.configuration.Data?.Areas);
  const LstServicios = useAppSelector((x) => x.configuration.Data?.ComboBox)?.filter((x) => x.Name === 'SysA_Servicios')[0].Data;
  const LstSedes = useAppSelector((x) => x.configuration.Data?.ComboBox)?.filter((x) => x.Name === 'SysI_Sedes')[0].Data;
  const Prm_Res = useAppSelector((x) => x.userInfo.Permissions?.Prm_Res);

  useEffect(() => {
    // Verificar si hay criterios de búsqueda válidos:
    // - Fechas válidas (no null y no vacías)
    // - O búsqueda por Paciente
    // - O búsqueda por Orden
    const hasFechasValidas = currentFilters?.Fechas && 
      currentFilters.Fechas.length === 2 && 
      currentFilters.Fechas[0] !== null && 
      currentFilters.Fechas[1] !== null;
    const hasPaciente = currentFilters?.Paciente && String(currentFilters.Paciente).trim() !== '';
    const hasOrden = currentFilters?.Orden && String(currentFilters.Orden).trim() !== '';
    const hasCriteriosBusqueda = hasFechasValidas || hasPaciente || hasOrden;

    // Solo ejecutar si hay criterios válidos y flag de redirección
    if (currentFilters && hasCriteriosBusqueda && currentFilters.Redirect && (currentFilters.UpdateFilter === undefined || currentFilters.UpdateFilter === true)) {
      // Crear un identificador único para estos filtros (incluir Paciente y Orden)
      const filtersKey = JSON.stringify({
        Fechas: currentFilters.Fechas,
        Estados: currentFilters.Estados,
        Servicios: currentFilters.Servicios,
        Areas: currentFilters.Areas,
        Estudios: currentFilters.Estudios,
        Paciente: currentFilters.Paciente,
        Orden: currentFilters.Orden,
        Redirect: currentFilters.Redirect
      });
      
      // Solo ejecutar si NO hemos ejecutado estos mismos filtros antes
      if (executedFiltersRef.current === filtersKey) {
        return; // Ya ejecutado, salir temprano
      }
      
      executedFiltersRef.current = filtersKey; // Marcar como ejecutado
      
      GetOrdersByFilter(currentFilters)
        .then((res) => {
          dispatch(setFilteredOrders(res));
          // Generar y guardar el texto de los filtros
          const filtersText = generateFiltersText(currentFilters, LstAreas, LstEstudios, LstServicios, LIS_States, LstSedes);
          dispatch(setFiltersToOrdersText(filtersText));
          
          // Solo abrir orden automáticamente si:
          // 1. Estamos en la página de órdenes
          // 2. Hay resultados
          // 3. El flag Redirect está activado (viene de dashboards u otra fuente externa)
          if (location.pathname === '/admin-orders' && res.length > 0 && currentFilters.Redirect) {
            // Si tiene permiso de resultados, ir a la pestaña 2 (Resultados) por defecto
            // ya que es la más utilizada por los usuarios
            if (Prm_Res) {
              dispatch(setCurrentActiveTab(2));
            }
            findOrder(undefined, res[0].Ord_Id);
            dispatch(setPanelVisible(true));
          }
        })
        .catch((error) => {
          console.error('Error al obtener órdenes filtradas:', error);
        })
        .finally(() => {
          // Limpiar el flag de redirección después de ejecutar
          dispatch(
            setFilteresToOrders({
              ...currentFilters,
              Redirect: false,
            })
          );
          // Resetear el ref para permitir futuras ejecuciones con nuevos filtros
          setTimeout(() => {
            executedFiltersRef.current = null;
          }, 500);
        });
    }
  }, [currentFilters, location.pathname, GetOrdersByFilter, findOrder, dispatch]);
};

export default useFilterAutoExecution;
